import numeral from "numeral";

import { useMemo } from "react";

import { AIRPORT_LIST } from "@/constants/airport";
import { maxLastSeenTime } from "@/constants/config";
import { bankCardSmallImg } from "@/constants/image-map";
import {
  BANK_IMG,
  BANK_NAME_MAP,
  PROGRAM_BUY_LINK,
  PROGRAM_CASH_LINK,
  tranfer_bank_to_program,
} from "@/constants/programs";
import { FlightItem } from "@/typings";

export function delay(time = 500): Promise<void> {
  return new Promise((resolve) => {
    setTimeout(() => {
      resolve();
    }, time);
  });
}

export function getTodayString() {
  const today = new Date();
  const year = today.getFullYear();
  const month = (today.getMonth() + 1).toString().padStart(2, "0"); // Months are zero-indexed
  const day = today.getDate().toString().padStart(2, "0");

  return `${year}-${month}-${day}`;
}

export function convertString(str: string) {
  return decodeURIComponent(encodeURIComponent(str));
}

export function Convert(s: string) {
  return s.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
}

export function formatMinutes(minutes: number) {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return `${h}h ${m}mins`;
}

export function formatPts(val: number) {
  if (val >= 10000000) {
    return `${numeral(val / 1000000).format("0,0")}M pts`;
  }
  if (val >= 1000000) {
    return `${numeral(val / 1000).format("0,0")}k pts`;
  }

  return `${numeral(val).format("0,0")} pts`;
}

export const transformerOptions = (
  map: Map<string, string>
): {
  label: string;
  value: string;
}[] => {
  const options = [];
  for (const [value, label] of map.entries()) {
    options.push({ label, value });
  }

  return options;
};

export const filterAirport = (
  keyword: string,
  {
    enableCustomCode = true,
    multiCityCodes = [],
  }:
    | {
        enableCustomCode?: boolean;
        multiCityCodes?: { code: string; description: string; airports: string[] }[];
      }
    | undefined = {}
) => {
  const ataMatchList = AIRPORT_LIST.filter((item) => {
    if (item === undefined || item === null || item.iata == null) {
      return false;
    }
    return item.iata.includes(keyword.toUpperCase());
  }).slice(0, 10);

  const cityFiestMatchList = AIRPORT_LIST.filter((item) => {
    if (item === undefined || item === null || item.city == null || ataMatchList.includes(item)) {
      return false;
    }
    return item.city.toUpperCase().startsWith(keyword.toUpperCase());
  }).slice(0, 10);

  const cityMatchList = AIRPORT_LIST.filter((item) => {
    if (
      item === undefined ||
      item === null ||
      item.city == null ||
      cityFiestMatchList.includes(item) ||
      ataMatchList.includes(item)
    ) {
      return false;
    }
    return item.city.toUpperCase().includes(keyword.toUpperCase());
  }).slice(0, 10);

  const fullNameMatchList = AIRPORT_LIST.filter((item) => {
    if (
      item === undefined ||
      item === null ||
      item.name == null ||
      cityFiestMatchList.includes(item) ||
      ataMatchList.includes(item) ||
      cityMatchList.includes(item)
    ) {
      return false;
    }
    return item.name.toUpperCase().includes(keyword.toUpperCase());
  }).slice(0, 10);

  const countryMatchList = AIRPORT_LIST.filter((item) => {
    if (
      item === undefined ||
      item === null ||
      item.city == null ||
      cityFiestMatchList.includes(item) ||
      ataMatchList.includes(item) ||
      cityMatchList.includes(item) ||
      fullNameMatchList.includes(item)
    ) {
      return false;
    }
    return item.country.toUpperCase().includes(keyword.toUpperCase());
  }).slice(0, 10);

  const labelMatchList = AIRPORT_LIST.filter((item) => {
    if (
      item === undefined ||
      item === null ||
      item.city == null ||
      cityFiestMatchList.includes(item) ||
      ataMatchList.includes(item) ||
      cityMatchList.includes(item) ||
      fullNameMatchList.includes(item) ||
      countryMatchList.includes(item)
    ) {
      return false;
    }
    return item.label ? item.label.toUpperCase().includes(keyword.toUpperCase()) : false;
  }).slice(0, 10);

  const customCodeList = multiCityCodes
    .map((item) => item.code)
    .filter((item) => item.toUpperCase().includes(keyword.toUpperCase()));

  // 防止卡顿 最多展示10条
  const concatenatedList = [
    ...(enableCustomCode ? customCodeList : []),
    ...ataMatchList,
    ...cityFiestMatchList,
    ...cityMatchList,
    ...fullNameMatchList,
    ...countryMatchList,
    ...labelMatchList,
  ].slice(0, 15);

  return concatenatedList;
};

export const isIntersection = (arr1: any[], arr2: any[]) => {
  return arr1.some((v) => arr2.includes(v));
};

export const getBankImgByProgram = (program: string): string[] => {
  const result: string[] = [];
  for (const [key, val] of tranfer_bank_to_program.entries()) {
    if (val.includes(program)) {
      Object.entries(BANK_NAME_MAP).some(([key2, value2]) => {
        if (value2 === key) {
          result.push(BANK_IMG[key2]);
        }
      });
    }
  }

  return result;
};

export const getBankNameByProgram = (program: string): string[] => {
  const result: string[] = [];
  for (const [key, val] of tranfer_bank_to_program.entries()) {
    if (val.includes(program)) {
      Object.entries(BANK_NAME_MAP).some(([key2, value2]) => {
        if (value2 === key) {
          result.push(key2);
        }
      });
    }
  }

  return result;
};

export const getBankInfoByProgram = (program: string): { name: string; img: string }[] => {
  const result: { name: string; img: string }[] = [];
  for (const [key, val] of tranfer_bank_to_program.entries()) {
    if (val.includes(program)) {
      Object.entries(BANK_NAME_MAP).some(([key2, value2]) => {
        if (value2 === key) {
          result.push({
            name: value2,
            img: bankCardSmallImg[key2],
          });
        }
      });
    }
  }

  return result;
};

export const sleep = (ms: number) => {
  return new Promise((resolve) => setTimeout(resolve, ms));
};

export const calculateAirlineUrlWithData = (data: FlightItem) => {
  const products = useMemo(() => data?.fare?.products || [], [data]);
  const origin_airport = products[0]?.origin;
  const arrival_airport = products[products.length - 1]?.destination;
  const departure_date = data?.date;
  const program_code = data?.program_code;
  const cabin = data?.payment?.cabin || data?.final_cabin_type;

  return calculateTargetUrl(program_code, origin_airport, arrival_airport, departure_date, cabin);
};

export const calculateTargetUrl = (
  program: string | undefined,
  origin_airport: string | undefined,
  arrival_airport: string | undefined,
  departure_date: string | undefined,
  cabin: string,
  use_cash = false
) => {
  // Get passenger from the url
  const urlParams = new URLSearchParams(window.location.search);
  const paxVal = urlParams.get("pax");
  const childrenVal = urlParams.get("children");

  const children = childrenVal ? parseInt(childrenVal) : 0;
  const passenger = paxVal ? parseInt(paxVal) + children : children;
  const adult = passenger - children;

  if (
    program == undefined ||
    origin_airport == undefined ||
    arrival_airport == undefined ||
    departure_date == undefined
  ) {
    return "";
  }

  // Get a set
  const CONVERT_DATE_SET = new Set(["EY", "SK", "KL"]);
  if (CONVERT_DATE_SET.has(program)) {
    // Convert date from YYYY-MM-DD to YYYYMMDD
    departure_date = departure_date.replace(/-/g, "");
  } else if (program === "VA") {
    // Convert date from YYYY-MM-DD to MM-DD-YYYY
    departure_date = departure_date.replace(/(\d{4})-(\d{2})-(\d{2})/g, "$2-$3-$1");
  } else if (program == "BA") {
    // Convert date from YYYY-MM-DD to MM-DD-YYYY
    departure_date = departure_date.replace(/(\d{4})-(\d{2})-(\d{2})/g, "$3/$2/$1");
  } else if (program == "LH") {
    // Convert date from YYYY-MM-DD to DD-MM-YYYY
    departure_date = departure_date.replace(/(\d{4})-(\d{2})-(\d{2})/g, "$2%2F$3%2F$1");
  }

  if (program === "KL") {
    if (cabin) {
      cabin = cabin.toUpperCase();
    } else {
      cabin = "ECONOMY";
    }
  }

  const TARGET_LINK_OBJ = use_cash ? PROGRAM_CASH_LINK : PROGRAM_BUY_LINK;
  const CASH_PROGRAM_SET = new Set(["AA", "UA", "AS", "B6", "DL", "KL", "VS", "TK"]);
  if (use_cash && !CASH_PROGRAM_SET.has(program)) {
    return "";
  }
  const url = TARGET_LINK_OBJ[program.toLocaleLowerCase()]
    .replace("{data.departure_airport}", origin_airport)
    .replace("{data.arrival_airport}", arrival_airport)
    .replace("{data.departure_date}", departure_date)
    .replace("{data.passengers}", passenger.toString())
    .replace("{data.passengers}", passenger.toString())
    .replace("{data.adult}", adult.toString())
    .replace("{data.children}", children.toString())
    .replace("{data.cabin}", cabin);

  return url
    .replace("{data.departure_airport}", origin_airport)
    .replace("{data.arrival_airport}", arrival_airport)
    .replace("{data.departure_date}", departure_date)
    .replace("{data.passengers}", passenger.toString())
    .replace("{data.adult}", adult.toString())
    .replace("{data.children}", children.toString())
    .replace("{data.cabin}", cabin);
};

export const shouldShowJustNow = (time: number) => Date.now() / 1000 - time < maxLastSeenTime;

export const getSeatsText = (seats: number) => {
  if (seats == -1 || !seats) {
    return "";
  }

  const text = "+ Seats" + " Left";
  const num = `${seats + (seats >= 9 ? "" : "")}`;

  return `${num}${text}`;
};
